import i18next from 'i18next'

/**
 * Supported languages are shared across apps. Namespaces (resources) differ per
 * app and are composed there; the language set + detection live here.
 */

const DEFAULT_LANGUAGE_CODE = 'en' as const

export type AppLanguageCode = 'en' | 'it'

export interface AppLanguage {
	label: string
	code: AppLanguageCode
}

export const DEFAULT_LANGUAGE = {
	label: 'English (EN)',
	code: DEFAULT_LANGUAGE_CODE
} as const satisfies AppLanguage

export const APP_LANGUAGES = [
	DEFAULT_LANGUAGE,
	{ label: 'Italiano (IT)', code: 'it' }
] as const satisfies readonly AppLanguage[]

export function getInitialAppLanguage(): AppLanguageCode {
	if (typeof window === 'undefined') {
		return DEFAULT_LANGUAGE.code
	}

	// `<html lang>` is deliberately not a source. On a static deployment the
	// shell is rendered once at build time, with no request to read, so it
	// always says `en` — and read first, it outranked the person's own pick
	// and their browser on every load. Where a server does render per request
	// it derives `lang` from the same cookie and the browser's
	// `Accept-Language` read here, so dropping it loses nothing.
	const language = resolveAppLanguageCode(
		getStoredLanguage() ??
			getCookieValue(document.cookie, 'i18nextLng') ??
			getBrowserLanguages()
	)
	// `syncAppLanguage` keeps the attribute right on every later change; this
	// is the first value, which nothing else writes. `<html>` carries
	// `suppressHydrationWarning`, so correcting it before hydration is safe.
	document.documentElement.lang = language
	return language
}

/**
 * The browser's whole preference list, in the `Accept-Language` shape
 * `resolveAppLanguageCode` reads — not `navigator.language` alone, which is
 * only the first entry: `ja,it;q=0.9` has to land on `it`, not the default.
 */
function getBrowserLanguages() {
	const { languages, language } = window.navigator
	return languages?.length ? languages.join(',') : language
}

export function detectAppLanguageFromRequest(
	request: Request
): AppLanguageCode {
	return resolveAppLanguageCode(
		getCookieValue(request.headers.get('cookie'), 'i18nextLng') ??
			request.headers.get('accept-language')
	)
}

export function resolveAppLanguageCode(
	value: string | null | undefined
): AppLanguageCode {
	if (!value) {
		return DEFAULT_LANGUAGE.code
	}

	const candidates = value
		.split(',')
		.map((candidate) => candidate.split(';')[0]?.trim().toLowerCase())
		.filter(Boolean)

	for (const candidate of candidates) {
		const language = APP_LANGUAGES.find((appLanguage) => {
			const normalizedCode = appLanguage.code.toLowerCase()
			return (
				candidate === normalizedCode ||
				candidate?.startsWith(`${normalizedCode}-`)
			)
		})

		if (language) {
			return language.code
		}
	}

	return DEFAULT_LANGUAGE.code
}

/**
 * Call `listener` after every language change, and return the function that
 * stops listening.
 *
 * This exists for the tab title. A router's context is a snapshot: it takes
 * `getInitialAppLanguage()` once when the router is built and nothing writes to
 * it again, so a `head()` that reads `match.context.appLanguage` kept producing
 * the title in the language the tab had opened in while the page under it had
 * already re-rendered in the newly picked one (OPSSTD-8813). A `head()` is not
 * a component and cannot subscribe through `useTranslation` — only the router
 * can recompute one — so the change has to reach the router as an event.
 *
 * Non-app codes are dropped rather than widened: i18next will happily report a
 * language we do not ship, and a listener typed on `AppLanguageCode` should not
 * be the place that discovers it.
 */
export function onAppLanguageChange(
	listener: (language: AppLanguageCode) => void
) {
	const handler = (language: string) => {
		if (isAppLanguageCode(language)) {
			listener(language)
		}
	}

	i18next.on('languageChanged', handler)
	return () => i18next.off('languageChanged', handler)
}

export function isAppLanguageCode(value: unknown): value is AppLanguageCode {
	return (
		typeof value === 'string' &&
		APP_LANGUAGES.some((language) => language.code === value)
	)
}

export const changeAppLanguage = async (nextLanguage: AppLanguageCode) => {
	if (typeof window !== 'undefined') {
		setStoredLanguage(nextLanguage)
		persistLanguageCookie(nextLanguage)
	}

	await syncAppLanguage(nextLanguage)
}

export function syncAppLanguage(nextLanguage: AppLanguageCode) {
	// `<html lang>` is set here rather than left to the root component, and it
	// is the only place that can be right. The attribute is rendered from route
	// context, which is a snapshot taken when the route loaded: changing the
	// language does not invalidate it, so the document stayed `lang="en"` under
	// a fully Italian interface for the life of the tab (OPSSTD-5459). A screen
	// reader picks its pronunciation from this attribute, so that is Italian
	// read with English phonetics.
	//
	// Every path that changes the language funnels through here, which is why
	// it is here and not in `changeAppLanguage` — the invite page sets the
	// language from the invitation's own locale without going through the
	// picker, and it needs the attribute updated just the same.
	if (typeof document !== 'undefined') {
		document.documentElement.lang = nextLanguage
	}

	return i18next.changeLanguage(nextLanguage)
}

function getCookieValue(cookieHeader: string | null, key: string) {
	if (!cookieHeader) {
		return
	}

	const cookies = cookieHeader.split(';')
	for (const cookie of cookies) {
		const [cookieKey, ...valueParts] = cookie.trim().split('=')
		if (cookieKey === key) {
			try {
				return decodeURIComponent(valueParts.join('='))
			} catch {
				return
			}
		}
	}
}

function getStoredLanguage() {
	try {
		return window.localStorage.getItem('i18nextLng')
	} catch {
		return
	}
}

function setStoredLanguage(language: AppLanguageCode) {
	try {
		window.localStorage.setItem('i18nextLng', language)
	} catch {
		// Ignore storage failures; i18next and the cookie fallback still update.
	}
}

function persistLanguageCookie(language: AppLanguageCode) {
	try {
		// biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API is not universally available yet, and this non-sensitive fallback only lets SSR pick the initial language.
		document.cookie = `i18nextLng=${language}; path=/; max-age=31536000; SameSite=Lax`
	} catch {
		// Ignore cookie failures; localStorage/i18next still carry the language.
	}
}
